import React, { useState } from "react";
import {
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Table,
    Input,
    Spinner
} from "reactstrap";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
    getMonthlyParkingExportTemplates,
    addMonthlyParkingExportTemplate,
    deleteMonthlyParkingExportTemplate
} from "helpers/fakebackend_helper";
import { BARCODE_PROXY_URL } from "helpers/url_helper";
import axios from "axios";

const ExportButton = ({ data = [], fileName = "ExportedData" }) => {
    const user = JSON.parse(localStorage.getItem("authUser"));

    const [isOpen, setIsOpen] = useState(false);
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(false);

    const [templates, setTemplates] = useState([]);
    const [templateName, setTemplateName] = useState("");

    const openModal = async () => {
        if (!data.length) return;
        setLoading(true);
        // Load columns from data
        const cols = Object.keys(data[0]).map(col => ({
            name: col,
            checked: true
        }));
        setColumns(cols);

        // Load templates
        const resp = await getMonthlyParkingExportTemplates(user.token);
        if (resp?.status === "success") {
            setTemplates(resp.data);
        }

        setLoading(false)

        setTemplateName("");
        setIsOpen(true);
    };

    const toggleColumn = (colName) => {
        setColumns(prev =>
            prev.map(c =>
                c.name === colName ? { ...c, checked: !c.checked } : c
            )
        );
    };

    const applyTemplate = (configArr) => {
        setColumns(prev =>
            prev.map(c => ({
                ...c,
                checked: configArr.includes(c.name)
            }))
        );
    };

    const handleSaveTemplate = async () => {
        const selectedCols = columns.filter(c => c.checked).map(c => c.name);
        const payload = {
            template_name: templateName,
            template_configs: JSON.stringify(selectedCols)
        };

        await addMonthlyParkingExportTemplate(user.token, payload);

        const refreshed = await getMonthlyParkingExportTemplates(user.token);
        if (refreshed?.status === "success") {
            setTemplates(refreshed.data);
        }

        setTemplateName("");
    };

    const handleDeleteTemplate = async (id) => {
        await deleteMonthlyParkingExportTemplate(user.token, { id });

        const refreshed = await getMonthlyParkingExportTemplates(user.token);
        if (refreshed?.status === "success") {
            setTemplates(refreshed.data);
        }
    };

    const fetchBarcodeImage = async (barcode) => {
        try {
            const url = `${BARCODE_PROXY_URL}?barcode=${barcode}`;
            const response = await axios.get(url, { responseType: "arraybuffer" });

            const contentType = response.headers["content-type"]; // ex: image/png

            let ext = "png";
            if (contentType.includes("jpeg")) ext = "jpeg";
            if (contentType.includes("jpg")) ext = "jpeg";
            if (contentType.includes("webp")) ext = "webp";
            if (contentType.includes("svg")) ext = "svg";

            return { buffer: new Uint8Array(response.data), extension: ext };
        } catch (err) {
            console.log("Barcode fetch error:", err);
            return null;
        }
    };

    const exportToExcel = async () => {
        setLoading(true);
        const selectedCols = columns.filter(c => c.checked).map(c => c.name);
        if (selectedCols.length === 0) {
            setLoading(false);
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Data");

        const headerRow = selectedCols.map(col =>
            col === "line_items_json" ? "Items" : col
        );
        sheet.addRow(headerRow);

        const transformLineItems = (jsonStr, quantity, item_type) => {
            if (!jsonStr) {
                if (quantity) return `${quantity} x ${item_type || "-"}`;
                return "-";
            }
            try {
                const items = JSON.parse(jsonStr);
                const showTotal = items.length > 1;
                return items
                    .map(item =>
                        `${item.quantity} x ${item.meta_data?.[0]?.value || item.name
                        }${showTotal ? ` ($${item.total})` : ""}`
                    )
                    .join("\n");
            } catch {
                return "Invalid data";
            }
        };

        const transformPaymentType = (t, check_number) => {
            if (!t) return "-";
            t = t.toLowerCase();

            if (t.startsWith("stripe")) {
                return "Stripe " + (t?.split("_")[1]?.replace(/_/g, " ") || "").trim();
            } else if (t.startsWith("cod") || t.startsWith("cash")) {
                return "Cash";
            } else if (t.startsWith("cheque")) {
                return "Check" + (check_number != null ? ` (#${check_number})` : "");
            } else if (t.startsWith("qbo")) {
                return "QBO";
            }

            return t;
        };

        for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
            const row = data[rowIndex];

            const rowValues = selectedCols.map(col => {
                if (col === "line_items_json") {
                    return transformLineItems(
                        row.line_items_json,
                        row.quantity,
                        row.item_type
                    );
                }

                if (col === "payment_type") {
                    return transformPaymentType(
                        row.payment_type,
                        row.check_number
                    );
                }

                if (col === "barcode_number") {
                    return ""; // do not show the string
                }

                return row[col];
            });

            sheet.addRow(rowValues);

            const excelRowIndex = rowIndex + 1;

            if (selectedCols.includes("barcode_number") && row.barcode_number) {
                sheet.getRow(excelRowIndex).height = 60;

                const result = await fetchBarcodeImage(row.barcode_number);

                if (result) {
                    const imageId = workbook.addImage({
                        buffer: result.buffer,
                        extension: result.extension,
                    });

                    sheet.addImage(imageId, {
                        tl: {
                            col: selectedCols.indexOf("barcode_number"),
                            row: excelRowIndex
                        },
                        ext: { width: 80, height: 80 }
                    });
                }
            }
        }

        sheet.columns.forEach(col => {
            let maxLength = 10;
            col.eachCell({ includeEmpty: true }, cell => {
                const val = cell.value ? cell.value.toString() : "";
                if (val.length > maxLength) maxLength = val.length;
            });
            col.width = Math.min(maxLength + 2, 60);
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${fileName}.xlsx`);
        setLoading(false);
        setIsOpen(false);
    };

    return (
        <>
            <Button className="export-gradient-bg" onClick={openModal} disabled={loading}>
                {loading ? <Spinner size="sm" /> : "Export"}
            </Button>

            <Modal isOpen={isOpen} toggle={() => setIsOpen(false)} size="lg">
                <ModalHeader toggle={() => setIsOpen(false)}>
                    Select Columns to Export
                </ModalHeader>

                <ModalBody>

                    {/* TEMPLATES SECTION */}
                    <div className="mb-3">
                        {templates.map((t) => (
                            <Button
                                key={t.id}
                                color="light"
                                className="me-2 mb-2"
                                onClick={() => applyTemplate(JSON.parse(t.template_configs))}
                            >
                                {t.template_name}
                                <span
                                    style={{
                                        marginLeft: 8,
                                        cursor: "pointer",
                                        color: "red",
                                        fontWeight: "bold"
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteTemplate(t.id);
                                    }}
                                >
                                    ✕
                                </span>
                            </Button>
                        ))}
                    </div>

                    {/* COLUMN SELECT TABLE */}
                    <Table bordered responsive>
                        <thead>
                            <tr>
                                <th>Include</th>
                                <th>Column Name</th>
                            </tr>
                        </thead>
                        <tbody>
                            {columns.map((col) => (
                                <tr key={col.name}>
                                    <td style={{ width: "100px" }}>
                                        <Input
                                            type="checkbox"
                                            checked={col.checked}
                                            onClick={() => toggleColumn(col.name)}
                                        />
                                    </td>
                                    <td>{col.name}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    {/* SAVE TEMPLATE SECTION */}
                    <div className="mt-3">
                        <Input
                            type="text"
                            placeholder="Template Name"
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                        />

                        {templateName.trim() !== "" && (
                            <Button
                                color="success"
                                className="mt-2"
                                onClick={handleSaveTemplate}
                            >
                                Save Template
                            </Button>
                        )}
                    </div>

                </ModalBody>

                <ModalFooter>
                    <Button color="secondary" onClick={() => setIsOpen(false)}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={exportToExcel} disabled={loading}>
                        {loading ? <Spinner size="sm" /> : "Export"}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default ExportButton;